import React from 'react';
import { Form, useFormikContext } from 'formik';
import { Button } from '../../Button/Button';
import { Input } from '../../Input/Input';
import { ImageUpload } from '../../ImageUpload/ImageUpload';
import { DatePicker } from '../../DatePicker/DatePicker';
import { RichTextEditor } from '../../RichTextEditor/RichTextEditor';
import { TextArea } from '../../TextArea/TextArea';
import { AddEventFormValues } from '../types';

interface EventFormFieldsProps {
  onCancel: () => void;
  isEditMode?: boolean;
  existingImageUrl?: string;
}

export const EventFormFields: React.FC<EventFormFieldsProps> = ({
  onCancel,
  isEditMode = false,
  existingImageUrl,
}) => {
  const formik = useFormikContext<AddEventFormValues>();

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    formik.handleBlur(e);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Set all fields as touched before submission
    Object.keys(formik.values).forEach((key) => {
      formik.setFieldTouched(key, true, true);
    });

    // Process rich text fields to ensure they're properly handled
    const richTextFields = ['option1', 'option2', 'option3'];
    richTextFields.forEach((field) => {
      const value = formik.values[field as keyof AddEventFormValues];
      // Convert null or undefined to empty string
      if (!value) {
        formik.setFieldValue(field, '');
        return;
      }

      if (typeof value === 'string') {
        // Check if we need to process the HTML
        if (value.includes('&lt;') && value.includes('&gt;')) {
          // Decode HTML entities in case they're present
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = value;
          const decodedValue = tempDiv.textContent || '';
          formik.setFieldValue(field, decodedValue);
        }
      }
    });

    formik.submitForm();
  };

  return (
    <Form noValidate onSubmit={handleSubmit}>
      <ImageUpload
        fieldName="image"
        existingImageUrl={isEditMode ? existingImageUrl : undefined}
        isRequired={!isEditMode}
      />
      <Input
        id="name"
        name="name"
        label="Name"
        placeholder="Enter the name"
        value={formik.values.name}
        onChange={formik.handleChange}
        onBlur={handleInputBlur}
        error={formik.touched.name ? formik.errors.name : undefined}
        touched={formik.touched.name}
      />
      <div className="date-fields">
        <DatePicker
          id="startDate"
          name="startDate"
          label="Start Date"
          value={formik.values.startDate}
          onChange={(date) => formik.setFieldValue('startDate', date)}
          onBlur={() => formik.setFieldTouched('startDate', true)}
          error={formik.touched.startDate ? (formik.errors.startDate as string) : undefined}
          touched={formik.touched.startDate}
        />
        <DatePicker
          id="endDate"
          name="endDate"
          label="End Date"
          value={formik.values.endDate}
          onChange={(date) => formik.setFieldValue('endDate', date)}
          onBlur={() => formik.setFieldTouched('endDate', true)}
          error={formik.touched.endDate ? (formik.errors.endDate as string) : undefined}
          touched={formik.touched.endDate}
        />
      </div>
      <TextArea
        id="description"
        name="description"
        label="Description"
        placeholder="Enter the description here"
        value={formik.values.description}
        onChange={formik.handleChange}
        onBlur={handleInputBlur}
        error={formik.touched.description ? formik.errors.description : undefined}
        touched={formik.touched.description}
      />
      <RichTextEditor
        id="option1"
        name="option1"
        label="Option 1"
        value={formik.values.option1 ?? ''}
        onChange={(value) => formik.setFieldValue('option1', value || '')}
        onBlur={() => formik.setFieldTouched('option1', true)}
        error={formik.touched.option1 ? formik.errors.option1 : undefined}
        touched={formik.touched.option1}
      />
      <RichTextEditor
        id="option2"
        name="option2"
        label="Option 2"
        value={formik.values.option2 ?? ''}
        onChange={(value) => formik.setFieldValue('option2', value || '')}
        onBlur={() => formik.setFieldTouched('option2', true)}
        error={formik.touched.option2 ? formik.errors.option2 : undefined}
        touched={formik.touched.option2}
      />
      <RichTextEditor
        id="option3"
        name="option3"
        label="Option 3"
        value={formik.values.option3 ?? ''}
        onChange={(value) => formik.setFieldValue('option3', value || '')}
        onBlur={() => formik.setFieldTouched('option3', true)}
        error={formik.touched.option3 ? formik.errors.option3 : undefined}
        touched={formik.touched.option3}
      />
      <div className="form-actions">
        <Button variant="button" className="outline cancel" type="button" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="button" type="submit">
          {isEditMode ? 'Update Event' : 'Add New Event'}
        </Button>
      </div>
    </Form>
  );
};
